import LegalLayout, { Section } from "@/components/LegalLayout";
import { SITE } from "@/data/site";

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      description="How Kinect Nova AI collects, uses, protects and shares your information. GDPR compliant and aligned with Apple App Store and Google Play requirements."
      path="/privacy"
      intro={`This Privacy Policy explains how ${SITE.name} ("we", "us", "our") collects, uses, discloses and safeguards your information when you use our mobile application and website (collectively, the "Service"). We are committed to protecting your privacy and complying with the EU General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), and the data-handling requirements of the Apple App Store and Google Play.`}
    >
      <Section heading="1. Information we collect">
        <p>We collect information you provide directly to us, including:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li><strong>Account information:</strong> name, email address, phone number and password.</li>
          <li><strong>Health information:</strong> medications, conditions, allergies, vitals and other data you choose to add to your Medical Passport or Health Dashboard.</li>
          <li><strong>Emergency contacts:</strong> the names and contact details of people in your Emergency Circle.</li>
          <li><strong>Location data:</strong> precise location, only when you trigger Emergency SOS or enable location sharing.</li>
          <li><strong>Usage & device data:</strong> app interactions, device model, operating system and crash diagnostics.</li>
        </ul>
        <p>With your explicit permission, we may also receive data from Apple HealthKit and Android Health Connect. This data is used only to provide app features and is never sold or used for advertising.</p>
      </Section>

      <Section heading="2. How we use your information">
        <ul className="list-disc space-y-1 pl-6">
          <li>To provide, maintain and improve the Service and its features.</li>
          <li>To send medication reminders, emergency alerts and service notifications.</li>
          <li>To enable communication and care coordination within your family.</li>
          <li>To provide customer support and respond to your requests.</li>
          <li>To detect, prevent and address security incidents and fraud.</li>
          <li>To comply with legal obligations.</li>
        </ul>
      </Section>

      <Section heading="3. Legal bases for processing (GDPR)">
        <p>Where the GDPR applies, we process your personal data on the following legal bases: your consent; performance of a contract with you; compliance with a legal obligation; and our legitimate interests in operating and improving the Service. Health data is processed only with your explicit consent.</p>
      </Section>

      <Section heading="4. How we share information">
        <p>We do <strong>not</strong> sell your personal information. We share data only:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>With members of your Emergency Circle, as directed by you (e.g. during an SOS event).</li>
          <li>With service providers who process data on our behalf under strict confidentiality agreements.</li>
          <li>When required by law, regulation or valid legal process.</li>
          <li>To protect the rights, safety and security of our users and the public.</li>
        </ul>
      </Section>

      <Section heading="5. Data retention">
        <p>We retain personal data only for as long as necessary to provide the Service and fulfil the purposes described in this policy, unless a longer retention period is required by law. You may request deletion of your data at any time (see Section 8).</p>
      </Section>

      <Section heading="6. Data security">
        <p>We protect your data using industry-standard safeguards, including encryption in transit and at rest, access controls and regular security reviews. No method of transmission or storage is 100% secure, but we work continuously to protect your information.</p>
      </Section>

      <Section heading="7. Children's privacy">
        <p>The Service is not directed to children under 13 (or the minimum age required in your jurisdiction). We do not knowingly collect personal data from children. If you believe a child has provided us with personal data, please contact us so we can delete it.</p>
      </Section>

      <Section heading="8. Your rights">
        <p>Depending on your location, you have the right to access, correct, export, restrict or delete your personal data, and to withdraw consent at any time. You can exercise these rights in the app, via our <a href="/data-deletion" className="text-primary hover:underline">Data Deletion Request</a> page, or by emailing {SITE.supportEmail}. EU residents may also lodge a complaint with their local data protection authority.</p>
      </Section>

      <Section heading="9. International transfers">
        <p>Your information may be processed in countries other than your own. Where we transfer personal data internationally, we use appropriate safeguards such as Standard Contractual Clauses to ensure your data remains protected.</p>
      </Section>

      <Section heading="10. Changes to this policy">
        <p>We may update this Privacy Policy from time to time. We will notify you of material changes through the app or by email and update the "Last updated" date above.</p>
      </Section>

      <Section heading="11. Contact us">
        <p>If you have questions about this Privacy Policy or your data, contact us at {SITE.supportEmail}.</p>
      </Section>
    </LegalLayout>
  );
}
